import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  RANGE_KEYS,
  type ChartType,
  type RangeKey,
} from "@chartandgo/shared";
import { CHART_TYPES } from "@chartandgo/shared";
import type { ChartCard as ChartCardModel } from "../store/workspace.js";
import type { Theme } from "../store/workspace.js";
import { useBars } from "../hooks/useBars.js";
import { useQuote } from "../hooks/QuotesContext.js";
import {
  changeDirection,
  formatChange,
  formatPercent,
  formatPrice,
  formatVolume,
} from "../lib/format.js";
import { CHART_TYPE_LABELS, RANGE_LABELS } from "../lib/labels.js";
import { calculateRangeChange } from "../lib/rangeChange.js";
import { PriceChart } from "./PriceChart.js";
import {
  ChevronDownIcon,
  CloseIcon,
  CollapseIcon,
  DragIcon,
  ExpandIcon,
} from "./Icons.js";

interface ChartCardProps {
  card: ChartCardModel;
  theme: Theme;
  onRemove: (id: string) => void;
  onUpdate: (id: string, changes: Partial<Omit<ChartCardModel, "id">>) => void;
  onMoveStart: (event: ReactPointerEvent<HTMLElement>) => void;
  onToggleExpand: () => void;
  expanded: boolean;
}

export function ChartCard({
  card,
  theme,
  onRemove,
  onUpdate,
  onMoveStart,
  onToggleExpand,
  expanded,
}: ChartCardProps) {
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    selectedRangeBars,
  } = useBars(card.symbol, card.range);
  const quote = useQuote(card.symbol);

  const bars = data?.bars ?? [];
  const displayPrice = quote?.price ?? bars.at(-1)?.close ?? null;
  const rangeChange = calculateRangeChange(selectedRangeBars, displayPrice);
  const dir = changeDirection(rangeChange?.change);

  return (
    <div className="card">
      <header className="card__header" onPointerDown={onMoveStart}>
        <div className="card__identity">
          <span className="card__drag" data-drag-handle aria-label="Move chart">
            <DragIcon />
          </span>
          <div className="card__title">
            <div className="card__symbol-row">
              <span className="card__symbol">{card.symbol}</span>
              {data?.delayed && (
                <span className="badge badge--muted" title="Bars are delayed at least 15 minutes on the free data plan">
                  delayed
                </span>
              )}
            </div>
            <span className="card__name" title={card.name}>
              {card.name}
            </span>
          </div>
        </div>

        <div className="card__quote">
          <span className="card__price">{formatPrice(displayPrice)}</span>
          {rangeChange && (
            <span
              className={`card__change card__change--${dir}`}
              title={`${RANGE_LABELS[card.range]} change`}
            >
              {formatChange(rangeChange.change)} (
              {formatPercent(rangeChange.changePercent)})
              <span className="card__change-range">
                {RANGE_LABELS[card.range]}
              </span>
            </span>
          )}
        </div>

        <div className="card__actions">
          <button
            type="button"
            className="icon-button card__action"
            onClick={onToggleExpand}
            aria-label={
              expanded
                ? `Restore ${card.symbol} chart size`
                : `Expand ${card.symbol} chart`
            }
            title={expanded ? "Restore size" : "Expand to full width"}
          >
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </button>
          <button
            type="button"
            className="icon-button card__action"
            onClick={() => onRemove(card.id)}
            aria-label={`Remove ${card.symbol} chart`}
            title="Remove chart"
          >
            <CloseIcon />
          </button>
        </div>
      </header>

      <div className="card__toolbar">
        <div
          className="segmented card__ranges-full"
          role="group"
          aria-label="Time range"
        >
          {RANGE_KEYS.map((range) => (
            <button
              key={range}
              type="button"
              className={`segmented__item ${
                card.range === range ? "segmented__item--active" : ""
              }`}
              onClick={() => onUpdate(card.id, { range })}
            >
              {RANGE_LABELS[range]}
            </button>
          ))}
        </div>
        <div className="card__ranges-compact">
          <RangeMenu
            value={card.range}
            onChange={(range) => onUpdate(card.id, { range })}
          />
        </div>

        <div className="card__toolbar-right">
          <ChartTypeMenu
            value={card.chartType}
            onChange={(chartType) => onUpdate(card.id, { chartType })}
          />
          <button
            type="button"
            className={`chip ${card.showVolume ? "chip--active" : ""}`}
            onClick={() => onUpdate(card.id, { showVolume: !card.showVolume })}
            aria-pressed={card.showVolume}
          >
            Volume
          </button>
        </div>
      </div>

      <div className="card__body">
        {isLoading && <div className="card__state">Loading chart…</div>}

        {isError && (
          <div className="card__state card__state--error">
            <p>{(error as Error).message}</p>
            <button type="button" className="chip" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        )}

        {!isLoading && !isError && bars.length === 0 && (
          <div className="card__state">
            No data available for this range.
          </div>
        )}

        {!isError && bars.length > 0 && (
          <PriceChart
            bars={bars}
            chartType={card.chartType}
            showVolume={card.showVolume}
            theme={theme}
            isLoadingEarlier={isFetchingNextPage}
            onLoadEarlier={
              hasNextPage && !isFetchingNextPage
                ? () => void fetchNextPage()
                : undefined
            }
          />
        )}
      </div>

      <footer className="card__footer">
        <span>
          {quote ? (
            <>
              Vol {formatVolume(quote.volume)} · IEX quote
            </>
          ) : (
            "Live quote unavailable"
          )}
        </span>
        {isFetching && (
          <span className="card__footer-status">
            {isFetchingNextPage ? "loading history…" : "updating…"}
          </span>
        )}
      </footer>
    </div>
  );
}

function RangeMenu({
  value,
  onChange,
}: {
  value: RangeKey;
  onChange: (range: RangeKey) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className="chip chip--menu"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {RANGE_LABELS[value]}
        <ChevronDownIcon width={14} height={14} />
      </button>
      {open && (
        <div className="menu__list menu__list--left" role="listbox">
          {RANGE_KEYS.map((range) => (
            <button
              key={range}
              type="button"
              role="option"
              aria-selected={range === value}
              className={`menu__item ${
                range === value ? "menu__item--active" : ""
              }`}
              onClick={() => {
                onChange(range);
                setOpen(false);
              }}
            >
              {RANGE_LABELS[range]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartTypeMenu({
  value,
  onChange,
}: {
  value: ChartType;
  onChange: (type: ChartType) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="menu" ref={ref}>
      <button
        type="button"
        className="chip chip--menu"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {CHART_TYPE_LABELS[value]}
        <ChevronDownIcon width={14} height={14} />
      </button>
      {open && (
        <div className="menu__list" role="listbox">
          {CHART_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              role="option"
              aria-selected={type === value}
              className={`menu__item ${type === value ? "menu__item--active" : ""}`}
              onClick={() => {
                onChange(type);
                setOpen(false);
              }}
            >
              {CHART_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
